// src/components/ChatMessage.js
import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, IconButton, useTheme } from '@mui/material';
import { keyframes } from '@emotion/react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

/* 4‑dot "bumper‑light" keyframes */
const flash = keyframes`
  0%, 80%, 100% { opacity: 0.3; }
  40%           { opacity: 1; }
`;

function extractDescription(text) {
  return text.split(/\r?\n\r?\n/)[0].trim();
}

export default function ChatMessage({ sender, text }) {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const toggleRef = useRef(null);

  // Determine if `text` is a React node (our injected link)
  const isReactNode = typeof text !== 'string';

  // String-based variables
  let fullText = '';
  let cleanText = '';
  let isLoading = false;
  let needsToggle = false;
  const MAX_LINES = 5;

  if (!isReactNode) {
    // 1. Normalize assistant text
    fullText =
      sender === 'ai'
        ? text.replace(/^\s*Changes:\s*/i, '').trimStart()
        : text;
    // 2. Description vs full
    cleanText =
      process.env.NODE_ENV === 'production' && sender === 'ai'
        ? extractDescription(fullText)
        : fullText;
    // 3. Loading placeholder
    isLoading = sender === 'ai' && text === 'Generating…';
    // 4. Toggle if long
    needsToggle =
      !isLoading &&
      (cleanText.split('\n').length > MAX_LINES ||
        cleanText.length > MAX_LINES * 80);
  }

  // Scroll chevron into view when expanded
  useEffect(() => {
    if (!isReactNode && toggleRef.current && expanded) {
      toggleRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [expanded, isReactNode]);

  return (
    <Box
      sx={{
        position: 'relative', p: 1.25, my: 1, borderRadius: 1,
        maxWidth: '80%',
        bgcolor: sender === 'user' ? '#e0e0e0' : '#fff',
        alignSelf: sender === 'user' ? 'flex-end' : 'flex-start',
        color: sender === 'ai' ? '#555' : 'black',
        display: 'flex', flexDirection: 'column',
      }}
    >
      {/* fade‑out hint */}
      {!isReactNode && !expanded && needsToggle && (
        <Box sx={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          height: theme.spacing(4),
          background: `linear-gradient(transparent, ${sender === 'user' ? '#e0e0e0' : '#fff'})`,
          pointerEvents: 'none', borderRadius: '0 0 8px 8px',
        }} />
      )}

      {/* message text or React node */}
      <Typography
        component="div"
        variant="body2"
        sx={{
          display: !isReactNode ? '-webkit-box' : 'block',
          WebkitLineClamp: expanded || isLoading ? 'none' : MAX_LINES,
          WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'pre-wrap',
        }}
      >
        {isReactNode ? text : cleanText}
      </Typography>

      {/* spinner */}
      {!isReactNode && isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
          {[0,1,2,3].map(i=>(
            <Box key={i} sx={{
              width:8, height:8, borderRadius:'50%', bgcolor:'grey.500',
              opacity:0.3, animation:`${flash} 1s ease-in-out ${i*0.2}s infinite`, mx:0.5
            }}/>
          ))}
        </Box>
      )}

      {/* expand/collapse chevron */}
      {!isReactNode && needsToggle && (
        <Box ref={toggleRef} sx={{ display:'flex', justifyContent:'center', mt:1 }}>
          <IconButton size="small" onClick={() => setExpanded(prev => !prev)}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      )}
    </Box>
  );
}
